// Approval Integration Component
// Provides integration points for other modules to trigger approval requests

import React, { useState, useEffect, useRef } from 'react';
import { 
  Button, 
  Input, 
  Textarea, 
  Select, 
  SelectItem, 
  DateInput, 
  Label 
} from '../../../components/ui';
import { useFlexibleList, useEntity } from '../../../hooks/useSWR';
import { mutate } from 'swr';
import { usePriorities } from '../hooks/usePriorities';
import { useCreateApprovalRequest, useUpdateApprovalRequest } from '../hooks/useApprovalRequests';
import { approvalRequestsConfig } from '../approval-requests/approvalRequestsConfig';

const ApprovalIntegration = ({ 
  entityType, 
  entityId, 
  entityData,
  entity, // Full entity object (e.g., document with approvalTemplateId)
  document, // Document object (for revisions - to get approvalTemplateId)
  onSubmit,
  onCancel,
  onError, // Optional error callback
  isLoading = false,
  existingApproval = null,
  isEditMode = false,
  isViewOnly = false
}) => {
  // Get user from auth context (for future use if needed)
  // const { user } = useAuth();

  // Use SWR hooks for data operations
  const createApprovalRequest = useCreateApprovalRequest();
  const updateApprovalRequest = useUpdateApprovalRequest();
  
  // Fetch approval template if entity has approvalTemplateId
  // Check both direct ID and nested relation
  // Handle both cases: entity.approvalTemplateId (direct) or entity.approvalTemplate.id (relation)
  // Also handle if entity is a revision - get template from document prop or entity.document
  // Also handle if entity is an array (shouldn't happen, but safety check)
  const approvalTemplateId = (entity && typeof entity === 'object' && !Array.isArray(entity))
    ? (
        // First check if entity has approvalTemplateId directly (for documents)
        entity.approvalTemplateId ?? 
        // Check if entity has approvalTemplate relation
        entity.approvalTemplate?.id ?? 
        // If document prop is provided (for revisions), check document.approvalTemplateId
        document?.approvalTemplateId ??
        document?.approvalTemplate?.id ??
        // If entity is a revision, check entity.document.approvalTemplateId
        entity.document?.approvalTemplateId ??
        // If entity is a revision, check entity.document.approvalTemplate.id
        entity.document?.approvalTemplate?.id ??
        null
      )
    : null;
  
  // Only fetch template when NOT in edit mode (templates are only used for new approvals)
  // Once an approval is created, users should be able to edit freely without template values being re-applied
  const { data: approvalTemplateFromAPI, isLoading: templateLoading } = useEntity(
    '/approval-templates',
    (!isEditMode && approvalTemplateId) ? approvalTemplateId : undefined // Only fetch template for new approvals
  );
  
  // Use API template (which includes steps) if available, otherwise fall back to relation
  // Also check document.approvalTemplate and entity.document.approvalTemplate for revisions
  // Only use template when NOT in edit mode
  const approvalTemplate = (!isEditMode) 
    ? (approvalTemplateFromAPI || 
       entity?.approvalTemplate || 
       document?.approvalTemplate ||
       entity?.document?.approvalTemplate || 
       null)
    : null; // Don't use template in edit mode
  

  // Helper function to get entity identifier (code, title, or ID)
  const getEntityIdentifier = () => {
    // Priority 1: Check entity prop directly for code (most reliable)
    if (entity?.code) {
      return entity.code;
    }
    // Priority 1b: Product Status Change - prefer product name over ID
    if (entityType === 'product-status-change' && entityData?.productName) {
      return entityData.productName;
    }
    // Priority 2: Check entityData for entity-specific codes
    if (entityData?.complaintCode) {
      return entityData.complaintCode;
    }
    if (entityData?.documentCode) {
      return entityData.documentCode;
    }
    if (entityData?.ncrCode) {
      return entityData.ncrCode;
    }
    if (entityData?.changeRequestCode) {
      return entityData.changeRequestCode;
    }
    if (entityData?.riskAssessmentCode) {
      return entityData.riskAssessmentCode;
    }
    if (entityData?.code) {
      return entityData.code;
    }
    // Priority 3: Fallback to titles from entityData
    if (entityData?.complaintTitle) {
      return entityData.complaintTitle;
    }
    if (entityData?.documentTitle) {
      return entityData.documentTitle;
    }
    if (entityData?.title) {
      return entityData.title;
    }
    // Priority 4: Check entity prop for title
    if (entity?.title) {
      return entity.title;
    }
    // Last fallback: use entity type with ID
    return `${entityType} #${entityId}`;
  };

  // Initialize form data - will be updated by template if available
  const getInitialFormData = () => {
    const entityIdentifier = getEntityIdentifier();

    // For product status change, use product name only (no numeric ID)
    let formattedIdentifier;
    if (entityType === 'product-status-change') {
      formattedIdentifier = entityIdentifier; // e.g. product name
    } else {
      // Format: "complaint (2025-31-COPY)" or "document (DOC-001)" etc.
      formattedIdentifier = entityIdentifier.includes('#') 
        ? entityIdentifier // If it already has #, use as is
        : `${entityType} (${entityIdentifier})`; // Otherwise add entity type with parentheses
    }

    return {
      title: `Review and Approval for ${formattedIdentifier}`,
      description: `Review and approval request for ${formattedIdentifier}`,
    priority: 'medium', // Default to Medium (as string to match Select value)
    approvalType: '', // No default - user must select
    workflowType: '', // No default - user must select
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days from now
    approvers: [], // Array of approver IDs
    approverType: 'user' // 'user' or 'department'
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData());

  // Fetch users and departments for approver selection
  const { data: users, isLoading: usersLoading, isError: usersError } = useFlexibleList('/users');
  const { data: departments, isLoading: departmentsLoading, isError: departmentsError } = useFlexibleList('/department');
  // Approver pickers should only offer active users
  const activeUsers = (users || []).filter((user) => user.userIsActive === true);
  
  // Fetch priorities from API
  const { priorities, isLoading: prioritiesLoading } = usePriorities();

  // Populate form with existing data when in edit mode (only once)
  const hasInitialized = useRef(false);
  const hasTemplateInitialized = useRef(false);
  
  // Initialize form with approval template data when available
  // IMPORTANT: Templates are ONLY used for NEW approvals (first time creation)
  // Once an approval is created, users can edit freely without template values being re-applied
  useEffect(() => {
    // Only initialize once when we have a template and not in edit mode
    // Don't apply template if we're editing an existing approval
    if (isEditMode) return; // Never apply template in edit mode
    
    // Only initialize once when we have a template
    if (approvalTemplate && !templateLoading && !hasTemplateInitialized.current) {
      
      // Extract approvers from template steps
      const templateApprovers = [];
      // Get approverType from first step (all steps should have same type in a template)
      const approverType = approvalTemplate.steps?.[0]?.approverType || 'user';
      
      if (approvalTemplate.steps && approvalTemplate.steps.length > 0) {
        approvalTemplate.steps.forEach(step => {
          // Only add approverId if it exists (required for 'user' type, optional for 'role'/'department')
          if (step.approverId) {
            templateApprovers.push(step.approverId.toString());
          }
          // Note: For 'role' or 'department' types, approverId might be null
          // The backend will resolve approvers at request time based on approverType
        });
      }
      
      // Calculate due date from template's defaultDueDays
      let dueDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      if (approvalTemplate.defaultDueDays) {
        dueDate = new Date(Date.now() + approvalTemplate.defaultDueDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }
      
      // Convert priorityId to priority name for the select
      let priorityValue = 'medium'; // default
      if (priorities && priorities.length > 0) {
        if (approvalTemplate.priorityId) {
          const priorityObj = priorities.find(p => p.id === approvalTemplate.priorityId);
          priorityValue = priorityObj ? priorityObj.name.toLowerCase() : 'medium';
        } else if (approvalTemplate.priority?.name) {
          priorityValue = approvalTemplate.priority.name.toLowerCase();
        }
      } else if (approvalTemplate.priority?.name) {
        // Use priority name directly if priorities list not loaded yet
        priorityValue = approvalTemplate.priority.name.toLowerCase();
      }
      
      // Get entity code if available (prioritize code over ID)
      const entityCode = entity?.code ||
                        entityData?.documentCode || 
                        entityData?.code || 
                        entityData?.complaintCode ||
                        entityData?.deviationCode ||
                        entityData?.riskAssessmentCode ||
                        entityData?.ncrCode ||
                        entityData?.changeRequestCode ||
                        '';
      
      // Get entity name from various possible fields
      const entityName = entity?.title ||
                        entityData?.documentTitle || 
                        entityData?.title || 
                        entityData?.name ||
                        entityData?.complaintTitle ||
                        '';
      
      // Build formatted identifier - ALWAYS use code if available, never use ID
      const formattedIdentifier = entityCode 
        ? `${entityType} ${entityCode}${entityName ? ` - ${entityName}` : ''}`
        : entityName 
          ? `${entityType} ${entityName}`
          : `${entityType} #${entityId}`; // Only use ID as last resort
      
      const newFormData = {
        title: `Review and Approval for ${formattedIdentifier}`,
        description: approvalTemplate.description || `Review and approval request for ${formattedIdentifier}`,
        priority: priorityValue,
        approvalType: approvalTemplate.approvalType || 'single',
        workflowType: approvalTemplate.workflowType || 'approval',
        dueDate: dueDate,
        approvers: templateApprovers,
        approverType: approverType
      };
      
      setFormData(newFormData);
      hasTemplateInitialized.current = true;
    }
    // Note: Removed approvalTemplateId from dependencies to prevent re-initialization
    // Template should only be applied once when creating a new approval
  }, [approvalTemplate, templateLoading, priorities, isEditMode, entityType, entityId, entityData]);
  
  // When no template is driving the priority, try to derive it from the linked entity's priorityId/priority
  // This is especially useful for CAPA, NCR, Risk Assessment, etc. that already have a priority set
  useEffect(() => {
    if (isEditMode) return; // Don't override when editing an existing approval
    if (hasTemplateInitialized.current) return; // Template already set priority
    if (!entityData) return;
    if (!priorities || priorities.length === 0) return;

    // Try to get priorityId from multiple possible shapes
    const entityPriorityId =
      entityData.priorityId ??
      entityData.priority_id ??
      entityData.priority?.id ??
      null;

    if (!entityPriorityId) {
      return;
    }

    const priorityObj = priorities.find(p => p.id === entityPriorityId);
    if (!priorityObj || !priorityObj.name) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      priority: priorityObj.name.toLowerCase()
    }));
  }, [entityData, priorities, isEditMode]);
  
  // Track the last existingApproval ID to detect when it changes (e.g., after save)
  const lastApprovalIdRef = useRef(null);
  
  useEffect(() => {
    // Initialize when in edit mode OR view-only mode and we have the approval data
    if ((isEditMode || isViewOnly) && existingApproval) {
      // Check if existingApproval has changed (e.g., after save/update)
      const approvalIdChanged = lastApprovalIdRef.current !== existingApproval.id;
      const approvalUpdated = approvalIdChanged && lastApprovalIdRef.current !== null;
      
      // Reset initialization flag if approval ID changed (new record or updated record)
      if (approvalUpdated || !hasInitialized.current) {
        hasInitialized.current = false;
        lastApprovalIdRef.current = existingApproval.id;
      }
      
      // Wait for priorities to load before setting priority value
      if (priorities && priorities.length > 0 && !hasInitialized.current) {
        // Convert priorityId to priority name for the select
        let priorityValue = 'medium'; // default
        if (existingApproval.priorityId) {
          const priorityObj = priorities.find(p => p.id === existingApproval.priorityId);
          priorityValue = priorityObj ? priorityObj.name.toLowerCase() : 'medium';
        } else if (existingApproval.priority?.name) {
          priorityValue = existingApproval.priority.name.toLowerCase();
        }
        
        // Initialize with existing approval data
        setFormData({
          title: existingApproval.title || '',
          description: existingApproval.description || '',
          priority: priorityValue,
          approvalType: existingApproval.approvalType || 'single',
          workflowType: existingApproval.workflowType || 'approval',
          dueDate: existingApproval.dueDate ? 
            new Date(existingApproval.dueDate).toISOString().split('T')[0] : 
            new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          approvers: existingApproval.steps?.map(step => step.approverId.toString()) || [],
          approverType: existingApproval.steps?.[0]?.approverType || 'user'
        });
        
        hasInitialized.current = true;
      }
    }
    // Don't reset flags here - let them persist for the component lifetime
    // They will be reset when the component unmounts or when explicitly needed
    // Removed approvalTemplateId from dependencies to prevent resetting form when editing
    // Note: We keep priorities in dependencies to initialize when priorities load, but hasInitialized prevents re-running
  }, [isEditMode, isViewOnly, existingApproval, priorities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate approvers are selected
    if (formData.approvers.length === 0) {
      alert('Please select at least one approver');
      return;
    }

    // Validate workflow type is selected
    if (!formData.workflowType || formData.workflowType === '') {
      alert('Please select a workflow type');
      return;
    }

    // Validate approval type is selected
    if (!formData.approvalType || formData.approvalType === '') {
      alert('Please select an approval type');
      return;
    }

    // Ensure priorities have been loaded before validating/saving
    if (prioritiesLoading || !priorities || priorities.length === 0) {
      alert('Priorities are still loading. Please wait a moment and try again.');
      return;
    }

    // Validate priority is selected
    if (!formData.priority || formData.priority === '' || formData.priority === 'undefined') {
      alert('Please select a priority from the dropdown');
      return;
    }

    const priorityId = getPriorityId(formData.priority);
    if (!priorityId) {
      console.error('Priority validation failed:', {
        formDataPriority: formData.priority,
        prioritiesAvailable: priorities.map(p => ({ id: p.id, name: p.name })),
        priorityType: typeof formData.priority
      });
      alert(`Please select a valid priority. Selected: "${formData.priority}". Available priorities: ${priorities.map(p => p.name).join(', ')}`);
      return;
    }
    

    // Map frontend form data to backend DTO format - only include allowed fields
    const backendData = {
      // Include code for updates, let the hook handle code generation for new approvals
      ...(isEditMode && existingApproval ? { code: existingApproval.code } : {}),
      title: formData.title,
      description: formData.description,
      // Do NOT hard-code statusId here; let the backend handle default/pending status
      priorityId: priorityId, // Use validated priorityId
      entityTypeId: getEntityTypeId(entityType),
      entityId: entityId,
      entityData: entityData,
      approvalType: formData.approvalType,
      workflowType: formData.workflowType,
      dueDate: formData.dueDate,
      approvers: formData.approvers, // Include approvers array
      approverType: formData.approverType // Include approver type
      // All other fields (id, requestedById, status, priority, entityType, etc.) are set by backend
      // Note: requiresReview, comments, and fileAttachments are no longer part of ApprovalRequest
    };
    
    try {
      if (isEditMode && existingApproval) {
        // Validate that approval is still in pending status before allowing update
        const statusId = existingApproval.statusId;
        const statusValue = typeof existingApproval.status === 'string' 
          ? existingApproval.status 
          : (existingApproval.status?.name || existingApproval.status);
        
        // statusId === null or statusId === 1 means pending (default state)
        // statusId === undefined also means pending (not set yet)
        const isPending = (statusId === null || statusId === undefined || statusId === 1);
        
        // Check status value if available
        const isPendingByStatus = !statusValue || statusValue.toLowerCase() === 'pending';
        
        // If statusId is set and not 1, or status value is set and not pending, reject
        if (!isPending || !isPendingByStatus) {
          const errorMsg = statusValue?.toLowerCase() === 'cancelled'
            ? 'Cannot edit approval request that has been cancelled.'
            : 'Cannot edit approval request that is not in pending status.';
          if (onError) {
            onError(new Error(errorMsg));
          } else {
            alert(errorMsg);
          }
          return;
        }
        
        // Update existing approval request with fieldsToRemove
        const updatedApproval = await updateApprovalRequest(existingApproval.id, backendData, approvalRequestsConfig.fieldsToRemove);
        console.log('Approval updated successfully:', updatedApproval);
        
        // Update form data with the returned approval data to reflect server-side changes
        if (updatedApproval && priorities && priorities.length > 0) {
          // Convert priorityId to priority name for the select
          let priorityValue = 'medium'; // default
          if (updatedApproval.priorityId) {
            const priorityObj = priorities.find(p => p.id === updatedApproval.priorityId);
            priorityValue = priorityObj ? priorityObj.name.toLowerCase() : 'medium';
          } else if (updatedApproval.priority?.name) {
            priorityValue = updatedApproval.priority.name.toLowerCase();
          }
          
          // Update form data with the returned values
          setFormData({
            title: updatedApproval.title || '',
            description: updatedApproval.description || '',
            priority: priorityValue,
            approvalType: updatedApproval.approvalType || 'single',
            workflowType: updatedApproval.workflowType || 'approval',
            dueDate: updatedApproval.dueDate ? 
              new Date(updatedApproval.dueDate).toISOString().split('T')[0] : 
              new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            approvers: updatedApproval.steps?.map(step => step.approverId.toString()) || [],
            approverType: updatedApproval.steps?.[0]?.approverType || 'user'
          });
          
          // Reset initialization flag so form can re-initialize if needed
          hasInitialized.current = false;
        }
        
        // Invalidate SWR cache to refresh the parent component's data
        if (approvalRequestsConfig.api?.swrInvalidation) {
          if (approvalRequestsConfig.api.swrInvalidation.mainList) {
            await mutate(approvalRequestsConfig.api.swrInvalidation.mainList);
          }
          if (approvalRequestsConfig.api.swrInvalidation.myRequests) {
            await mutate(approvalRequestsConfig.api.swrInvalidation.myRequests);
          }
        }
        
        if (onSubmit) {
          await onSubmit(updatedApproval);
        }
      } else {
        // For new approvals, let the parent component handle creation via onSubmit prop
        // This prevents duplicate creation since GenericApprovalIntegration also calls createApprovalRequest
        if (onSubmit) {
          await onSubmit(backendData);
        } else {
          // Fallback: create directly if no onSubmit prop (standalone usage)
          const newApproval = await createApprovalRequest(backendData, approvalRequestsConfig.fieldsToRemove);
          console.log('Approval created successfully:', newApproval);
        }
      }
    } catch (error) {
      console.error('Error saving approval request:', error);
      alert(`Error saving approval request: ${error.message}`);
    }
  };

  // Helper function to get priority ID from priority name or ID
  const getPriorityId = (priority) => {
    // If priority is already a number (ID), validate it exists and return it
    if (typeof priority === 'number' || (!isNaN(priority) && priority !== '')) {
      const numericId = typeof priority === 'number' ? priority : parseInt(priority, 10);
      const priorityObj = priorities?.find(p => p.id === numericId);
      if (priorityObj) {
        return numericId;
      }
      // If numeric ID not found, continue to try name matching
    }
    
    // If priority is empty or undefined, return null
    if (!priority || priority === '' || priority === 'undefined' || priority === null) {
      console.warn('getPriorityId: priority is empty or undefined', { priority, priorities });
      return null;
    }
    
    // If priorities array is empty or not loaded, return null
    if (!priorities || priorities.length === 0) {
      console.warn('getPriorityId: priorities array is empty', { priority, priorities });
      return null;
    }
    
    const priorityStr = String(priority).toLowerCase().trim();
    
    // Try to find by exact lowercase match first
    let priorityObj = priorities.find(p => 
      p.name && p.name.toLowerCase() === priorityStr
    );
    
    // If not found, try partial match (e.g., "medium-low" matches "Medium-Low")
    if (!priorityObj) {
      priorityObj = priorities.find(p => 
        p.name && p.name.toLowerCase().replace(/\s+/g, '-') === priorityStr.replace(/\s+/g, '-')
      );
    }
    
    // If still not found, try matching without special characters
    if (!priorityObj) {
      const normalizedPriority = priorityStr.replace(/[^a-z0-9]/g, '');
      priorityObj = priorities.find(p => {
        if (!p.name) return false;
        const normalizedName = p.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalizedName === normalizedPriority;
      });
    }
    
    console.log('getPriorityId:', { 
      input: priority,
      inputType: typeof priority,
      normalizedInput: priorityStr,
      foundPriority: priorityObj ? { id: priorityObj.id, name: priorityObj.name } : null,
      prioritiesCount: priorities?.length,
      allPriorityNames: priorities?.map(p => ({ id: p.id, name: p.name }))
    });
    
    return priorityObj ? priorityObj.id : null;
  };

  // Helper function to map entity types to IDs
  const getEntityTypeId = (entityType) => {
    const entityTypeMap = {
      'document': 1,              // Document
      'document_revision': 1,     // Document revisions also use Document entity type
      'revision': 2,              // Revision
      'capa': 3,                  // CAPA
      'risk_assessment': 4,       // Risk Assessment
      'non_conformity': 5,        // Non-Conformity
      'non_conformity_report': 5, // Non-Conformity Report
      'change_request': 6,        // Change Request
      'complaint': 7,             // Complaint
      'supplier': 8,              // Supplier
      'supplier_rating': 9,    // Supplier Rating
      'training': 10,          // Training
      'todo': 11,              // Todo
      'capa_actions': 12,       // CAPA Actions
      'risk_mitigation': 13,   // Risk Mitigation Actions
      'complaint_actions': 14, // Complaint Actions
      'file_attachments': 15,   // FileAttachments
      'product-status-change': 16, // Product Status Change
      'product_status_change': 16,  // Product Status Change (alternative format)
      'deviation': 17,              // Deviation
    };
    return entityTypeMap[entityType] || 1; // Default to document
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value,
        // Reset approvers when approval type changes
        ...(name === 'approvalType' && { approvers: [] })
      };
      
      // Update title and description based on workflow type
      if (name === 'workflowType') {
        // For product-status-change, prioritize productName over code/ID
        let entityIdentifier = '';
        if (entityType === 'product-status-change' && entityData?.productName) {
          entityIdentifier = entityData.productName;
        } else {
          // Get entity code if available (prioritize code over ID)
          // Check entity prop first, then entityData
          const entityCode = entity?.code ||
                            entityData?.documentCode || 
                            entityData?.code || 
                            entityData?.complaintCode ||
                            entityData?.deviationCode ||
                            entityData?.riskAssessmentCode ||
                            entityData?.ncrCode ||
                            entityData?.changeRequestCode ||
                            '';
          
          // Get entity name from various possible fields
          const entityName = entity?.title ||
                            entityData?.documentTitle || 
                            entityData?.title || 
                            entityData?.name ||
                            entityData?.complaintTitle ||
                            '';
          
          // Build entity reference string - ALWAYS use code if available, never use ID
          entityIdentifier = entityCode 
            ? `${entityType} ${entityCode}${entityName ? ` - ${entityName}` : ''}`
            : entityName 
              ? `${entityType} ${entityName}`
              : `${entityType} #${entityId}`; // Only use ID as last resort
        }
        
        // Build title - for product-status-change use productName only, otherwise use entityIdentifier
        const titleEntityRef = (entityType === 'product-status-change' && entityData?.productName)
          ? entityData.productName
          : entityIdentifier;
        
        // For product-status-change, use productName in description too
        const descriptionEntityRef = (entityType === 'product-status-change' && entityData?.productName)
          ? entityData.productName
          : entityIdentifier;
        
        switch (value) {
          case 'review':
            newFormData.title = `Review Request for ${titleEntityRef}`;
            newFormData.description = `This is a review request for ${descriptionEntityRef}. Please review the document and provide your feedback.`;
            break;
          case 'approval':
            newFormData.title = `Approval Request for ${titleEntityRef}`;
            newFormData.description = `This is an approval request for ${descriptionEntityRef}. Please review and approve if it meets all requirements.`;
            break;
          case 'review_approval':
            newFormData.title = `Review and Approval Request for ${titleEntityRef}`;
            newFormData.description = `This is a review and approval request for ${descriptionEntityRef}. Please review the document first, then provide your approval.`;
            break;
          default:
            newFormData.title = `Review and Approval Request for ${titleEntityRef}`;
            newFormData.description = `This is a review and approval request for ${descriptionEntityRef}.`;
            break;
        }
      }
      
      return newFormData;
    });
  };

  const handleApproverChange = (approverId, isSelected) => {
    setFormData(prev => {
      if (isSelected) {
        return {
          ...prev,
          approvers: [...prev.approvers, approverId]
        };
      } else {
        return {
          ...prev,
          approvers: prev.approvers.filter(id => id !== approverId)
        };
      }
    });
  };

  // Helper function to get display text for select values
  const getDisplayText = (value, options, getLabel) => {
    if (!value) return 'Not set';
    const option = options?.find(opt => {
      if (typeof opt === 'object') {
        return getLabel ? getLabel(opt) === value : (opt.value === value || opt.id?.toString() === value?.toString());
      }
      return opt === value;
    });
    return option ? (getLabel ? getLabel(option) : (option.label || option.name || option)) : value;
  };

  // Helper function to get approver names for display
  const getApproverNames = () => {
    if (!formData.approvers || formData.approvers.length === 0) return [];
    
    if (formData.approverType === 'department') {
      return formData.approvers.map(deptId => {
        const dept = departments?.find(d => d.id.toString() === deptId.toString());
        return dept?.name || `Department #${deptId}`;
      });
    } else {
      return formData.approvers.map(userId => {
        const user = users?.find(u => u.id.toString() === userId.toString());
        return user?.userDisplayName || `${user?.userFirstName || ''} ${user?.userLastName || ''}`.trim() || user?.userEmail || `User #${userId}`;
      });
    }
  };

  const getApproverFields = () => {
    const { approverType } = formData;
    
    // In view-only mode, show approvers as a list
    if (isViewOnly) {
      const approverNames = getApproverNames();
      if (approverNames.length === 0) {
        return (
          <div>
            <Label>Approvers</Label>
            <div className="text-sm text-gray-500 py-2">No approvers assigned</div>
          </div>
        );
      }
      
      return (
        <div>
          <Label>Approvers ({approverNames.length})</Label>
          <div className="mt-2 space-y-1">
            {approverNames.map((name, index) => (
              <div key={index} className="text-sm text-gray-700 dark:text-gray-300 py-1 px-3 bg-gray-50 dark:bg-gray-800 rounded border">
                {index + 1}. {name}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Show the appropriate selection based on type and approval workflow
    // Note: Approver Type is now in the main form layout, not here
    const getSelectionFields = () => {
      if (approverType === 'department') {
        return getDepartmentSelectionFields();
      } else {
        return getUserSelectionFields();
      }
    };

    return (
      <div className="space-y-4">
        {getSelectionFields()}
      </div>
    );
  };

  const getDepartmentSelectionFields = () => {
    const { approvalType } = formData;
    
    switch (approvalType) {
      case 'single':
        return (
          <div>
            <Label htmlFor="singleDepartment" required>
              Select Department
            </Label>
            <Select
              id="singleDepartment"
              name="singleDepartment"
              value={formData.approvers[0] || ''}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  approvers: e.target.value ? [e.target.value] : []
                }));
              }}
              disabled={departmentsLoading || isViewOnly}
            >
              <SelectItem value="">Select a department</SelectItem>
              {departments?.map((dept) => (
                <SelectItem key={dept.id} value={dept.id.toString()}>
                  {dept.name}
                </SelectItem>
              ))}
            </Select>
            {departmentsLoading && <div className="text-xs text-gray-500 mt-1">Loading departments...</div>}
            {departmentsError && <div className="text-xs text-red-500 mt-1">Error loading departments</div>}
          </div>
        );
      
      case 'sequential':
        return (
          <div>
            <Label htmlFor="sequentialDepartments" required>
              Select Departments (in order)
            </Label>
            <div className="space-y-2">
              {formData.approvers.map((deptId, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-8">{index + 1}.</span>
                  <Select
                    value={deptId}
                    onChange={(e) => {
                      const newApprovers = [...formData.approvers];
                      newApprovers[index] = e.target.value;
                      setFormData(prev => ({ ...prev, approvers: newApprovers }));
                    }}
                    disabled={departmentsLoading || isViewOnly}
                    className="flex-1"
                  >
                    <SelectItem value="">Select department</SelectItem>
                    {departments?.filter(dept => !formData.approvers.includes(dept.id.toString()) || dept.id.toString() === deptId)
                      .map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.name}
                        </SelectItem>
                      ))}
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newApprovers = formData.approvers.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, approvers: newApprovers }));
                    }}
                    disabled={isViewOnly}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData(prev => ({ ...prev, approvers: [...prev.approvers, ''] }));
                }}
                disabled={departmentsLoading || isViewOnly}
              >
                Add Department
              </Button>
            </div>
            {departmentsLoading && <div className="text-xs text-gray-500 mt-1">Loading departments...</div>}
            {departmentsError && <div className="text-xs text-red-500 mt-1">Error loading departments</div>}
          </div>
        );
      
      case 'parallel':
        return (
          <div>
            <Label htmlFor="parallelDepartments" required>
              Select Departments (all must approve)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {departments?.map((dept) => (
                <div key={dept.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    id={`dept-${dept.id}`}
                    checked={formData.approvers.includes(dept.id.toString())}
                    onChange={(e) => handleApproverChange(dept.id.toString(), e.target.checked)}
                    disabled={departmentsLoading || isViewOnly}
                    className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`dept-${dept.id}`} className="text-sm cursor-pointer flex-1">
                    {dept.name}
                  </label>
                </div>
              ))}
            </div>
            {departmentsLoading && <div className="text-xs text-gray-500 mt-1">Loading departments...</div>}
            {departmentsError && <div className="text-xs text-red-500 mt-1">Error loading departments</div>}
          </div>
        );
      
      case 'any_one':
        return (
          <div>
            <Label htmlFor="anyOneDepartments" required>
              Select Departments (any one can approve)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {departments?.map((dept) => (
                <div key={dept.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    id={`dept-${dept.id}`}
                    checked={formData.approvers.includes(dept.id.toString())}
                    onChange={(e) => handleApproverChange(dept.id.toString(), e.target.checked)}
                    disabled={departmentsLoading || isViewOnly}
                    className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`dept-${dept.id}`} className="text-sm cursor-pointer flex-1">
                    {dept.name}
                  </label>
                </div>
              ))}
            </div>
            {departmentsLoading && <div className="text-xs text-gray-500 mt-1">Loading departments...</div>}
            {departmentsError && <div className="text-xs text-red-500 mt-1">Error loading departments</div>}
          </div>
        );
      
      default:
        return null;
    }
  };

  const getUserSelectionFields = () => {
    const { approvalType } = formData;
    
    switch (approvalType) {
      case 'single':
        return (
          <div>
            <Label htmlFor="singleApprover" required>
              Select Approver
            </Label>
            <Select
              id="singleApprover"
              name="singleApprover"
              value={formData.approvers[0] || ''}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  approvers: e.target.value ? [e.target.value] : []
                }));
              }}
              disabled={usersLoading || isViewOnly}
            >
              <SelectItem value="">Select an approver</SelectItem>
              {activeUsers.map((user) => (
                <SelectItem key={user.id} value={user.id.toString()}>
                  {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                </SelectItem>
              ))}
            </Select>
            {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
            {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
          </div>
        );
      
      case 'sequential':
        return (
          <div>
            <Label htmlFor="sequentialApprovers" required>
              Select Approvers (in order)
            </Label>
            <div className="space-y-2">
              {formData.approvers.map((approverId, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm font-medium w-8">{index + 1}.</span>
                  <Select
                    value={approverId}
                    onChange={(e) => {
                      const newApprovers = [...formData.approvers];
                      newApprovers[index] = e.target.value;
                      setFormData(prev => ({ ...prev, approvers: newApprovers }));
                    }}
                    disabled={usersLoading || isViewOnly}
                    className="flex-1"
                  >
                    <SelectItem value="">Select approver</SelectItem>
                    {activeUsers.filter(user => !formData.approvers.includes(user.id.toString()) || user.id.toString() === approverId)
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                        </SelectItem>
                      ))}
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newApprovers = formData.approvers.filter((_, i) => i !== index);
                      setFormData(prev => ({ ...prev, approvers: newApprovers }));
                    }}
                    disabled={isViewOnly}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData(prev => ({ ...prev, approvers: [...prev.approvers, ''] }));
                }}
                disabled={usersLoading || isViewOnly}
              >
                Add Approver
              </Button>
            </div>
            {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
            {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
          </div>
        );
      
      case 'parallel':
        return (
          <div>
            <Label htmlFor="parallelApprovers" required>
              Select Approvers (all must approve)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {activeUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    id={`approver-${user.id}`}
                    checked={formData.approvers.includes(user.id.toString())}
                    onChange={(e) => handleApproverChange(user.id.toString(), e.target.checked)}
                    disabled={usersLoading || isViewOnly}
                    className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`approver-${user.id}`} className="text-sm cursor-pointer flex-1">
                    {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                  </label>
                </div>
              ))}
            </div>
            {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
            {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
          </div>
        );
      
      case 'any_one':
        return (
          <div>
            <Label htmlFor="anyOneApprovers" required>
              Select Approvers (any one can approve)
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {activeUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    id={`approver-${user.id}`}
                    checked={formData.approvers.includes(user.id.toString())}
                    onChange={(e) => handleApproverChange(user.id.toString(), e.target.checked)}
                    disabled={usersLoading || isViewOnly}
                    className="rounded w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor={`approver-${user.id}`} className="text-sm cursor-pointer flex-1">
                    {user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim() || user.userEmail}
                  </label>
                </div>
              ))}
            </div>
            {usersLoading && <div className="text-xs text-gray-500 mt-1">Loading users...</div>}
            {usersError && !usersLoading && (!users || users.length === 0) && <div className="text-xs text-red-500 mt-1">Error loading users</div>}
          </div>
        );
      
      default:
        return null;
    }
  };



  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        {/* Title */}
        <div>
          <Label htmlFor="title" required>
            Request Title
          </Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            placeholder="Enter approval request title"
            disabled={isViewOnly}
            readOnly={isViewOnly}
          />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description" required>
            Description
          </Label>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={3}
            placeholder="Describe what needs approval"
            disabled={isViewOnly}
            readOnly={isViewOnly}
          />
        </div>

        {/* Priority and Due Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="priority">
              Priority
            </Label>
            {isViewOnly ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded border">
                {formData.priority ? (priorities?.find(p => p.name.toLowerCase() === formData.priority)?.name || formData.priority) : 'Not set'}
              </div>
            ) : (
              <>
                <Select
                  id="priority"
                  name="priority"
                  value={formData.priority || ''}
                  onChange={handleInputChange}
                  disabled={prioritiesLoading || isViewOnly}
                  required
                >
                  {prioritiesLoading ? (
                    <SelectItem value="">Loading priorities...</SelectItem>
                  ) : (
                    <>
                      <SelectItem value="">Select a priority</SelectItem>
                      {priorities && priorities.map(priority => (
                        <SelectItem key={priority.id} value={priority.name.toLowerCase()}>
                          {priority.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </Select>
                {!prioritiesLoading && (!priorities || priorities.length === 0) && (
                  <div className="text-xs text-red-500 mt-1">No priorities available. Please contact your administrator.</div>
                )}
              </>
            )}
          </div>

          <div>
            <Label htmlFor="dueDate" required>
              Due Date
            </Label>
            <DateInput
              id="dueDate"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleInputChange}
              required
              disabled={isViewOnly}
              readOnly={isViewOnly}
            />
          </div>

          {/* Empty space - 1/3 */}
          <div></div>
        </div>

        {/* Workflow Type, Approval Type, and Approver Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="workflowType" required>
              Workflow Type
            </Label>
            {isViewOnly ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded border">
                {formData.workflowType === 'review' ? 'Review Only' :
                 formData.workflowType === 'approval' ? 'Approval Only' :
                 formData.workflowType === 'review_approval' ? 'Review and Approval' :
                 'Not set'}
              </div>
            ) : (
              <Select
                id="workflowType"
                name="workflowType"
                value={formData.workflowType}
                onChange={handleInputChange}
                disabled={isViewOnly}
              >
                <SelectItem value="">Select Workflow Type</SelectItem>
                <SelectItem value="review">Review Only</SelectItem>
                <SelectItem value="approval">Approval Only</SelectItem>
                <SelectItem value="review_approval">Review and Approval</SelectItem>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="approvalType">
              Approval Type
            </Label>
            {isViewOnly ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded border">
                {formData.approvalType === 'single' ? 'Single Approver' :
                 formData.approvalType === 'sequential' ? 'Sequential Approval' :
                 formData.approvalType === 'parallel' ? 'Parallel Approval' :
                 formData.approvalType === 'any_one' ? 'Any One Approver' :
                 'Not set'}
              </div>
            ) : (
              <Select
                id="approvalType"
                name="approvalType"
                value={formData.approvalType}
                onChange={handleInputChange}
                disabled={isViewOnly}
              >
                <SelectItem value="">Select Approval Type</SelectItem>
                <SelectItem value="single">Single Approver</SelectItem>
                <SelectItem value="sequential">Sequential Approval</SelectItem>
                <SelectItem value="parallel">Parallel Approval</SelectItem>
                <SelectItem value="any_one">Any One Approver</SelectItem>
              </Select>
            )}
          </div>

          <div>
            <Label htmlFor="approverType" required>
              Approver Type
            </Label>
            {isViewOnly ? (
              <div className="text-sm text-gray-700 dark:text-gray-300 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded border">
                {formData.approverType === 'user' ? 'Specific Users' :
                 formData.approverType === 'department' ? 'Department' :
                 'Not set'}
              </div>
            ) : (
              <Select
                id="approverType"
                name="approverType"
                value={formData.approverType}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    approverType: e.target.value,
                    approvers: [] // Reset approvers when type changes
                  }));
                }}
                disabled={isViewOnly}
              >
                <SelectItem value="user">Specific Users</SelectItem>
                <SelectItem value="department">Department</SelectItem>
              </Select>
            )}
          </div>
        </div>
        <div className="mb-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
          {(() => {
            const productName =
              entityType === 'product-status-change' && entityData?.productName
                ? entityData.productName
                : null;

            if (formData.workflowType === 'review') {
              return productName
                ? `${productName} will be reviewed but not approved`
                : 'Document will be reviewed but not approved';
            }
            if (formData.workflowType === 'approval') {
              return productName
                ? `${productName} will be approved directly without review`
                : 'Document will be approved directly without review';
            }
            if (formData.workflowType === 'review_approval') {
              return productName
                ? `${productName} will be reviewed first, then approved`
                : 'Document will be reviewed first, then approved';
            }
            return null;
          })()}
        </div>

        {/* Approver Selection */}
        {getApproverFields()}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          {isViewOnly ? 'Close' : 'Cancel'}
        </Button>
        {!isViewOnly && (
          <Button
            type="submit"
            variant="default"
            disabled={isLoading}
          >
            {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Approval Request' : 'Create Approval Request')}
          </Button>
        )}
      </div>
    </form>
  );
};

export default ApprovalIntegration;